import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { WORKSHOP_URL } from "@/lib/defaults";
import { trackCta } from "@/lib/analytics";

const withUtm = (rawUrl, utmSource) => {
    if (!rawUrl) return "";
    try {
        const u = new URL(rawUrl);
        u.searchParams.set("utm_source", utmSource);
        return u.toString();
    } catch {
        const sep = rawUrl.includes("?") ? "&" : "?";
        return `${rawUrl}${sep}utm_source=${encodeURIComponent(utmSource)}`;
    }
};

const navLinks = [
    { label: "Home", target: "home", testId: "nav-home" },
    { label: "Services", target: "services", testId: "nav-services" },
    {
        label: "Research Workshops",
        target: "research-workshops",
        testId: "nav-research-workshops",
        external: true,
        utm: "aurea-nav",
    },
    { label: "AWSAI", target: "awsai", testId: "nav-awsai" },
    { label: "SMART-WORK", target: "smart-work", testId: "nav-smart-work" },
    { label: "Books & Resources", target: "books-resources", testId: "nav-books-resources" },
    { label: "Contact", target: "contact", testId: "nav-contact" },
];

export const Navigation = () => {
    const { content } = useContent();
    const awsaiUrl = content.hero.primary_cta_url; // central AWSAI URL
    const workshopBase = content.featured_programme?.workshop_url || WORKSHOP_URL;
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const hrefFor = (link) => {
        if (link.external) return withUtm(workshopBase, link.utm || "aurea-nav");
        return `#${link.target}`;
    };

    const handleNavClick = (e, link) => {
        if (link.external) {
            // Let the browser follow the external link in a new tab (target=_blank).
            trackCta("workshop_click", { source: "nav" });
            setMobileOpen(false);
            return;
        }
        e.preventDefault();
        const el = document.getElementById(link.target);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setMobileOpen(false);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-md border-b border-slate-200"
                    : "bg-white/85 backdrop-blur-sm border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#home"
                    data-testid="nav-logo"
                    onClick={(e) => handleNavClick(e, { target: "home" })}
                    className="flex items-center gap-2.5"
                >
                    <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0A192F] text-white font-display font-semibold">A</span>
                    <div className="flex flex-col leading-tight">
                        <span className="font-display font-semibold text-slate-900 text-[15px] tracking-tight">Aurea Digital Solutions</span>
                        <span className="text-[10.5px] uppercase tracking-[0.18em] text-teal-700 font-medium">Advisory · Diagnostics · Knowledge</span>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
                    {navLinks.map((link) => (
                        <a
                            key={link.target}
                            href={hrefFor(link)}
                            target={link.external ? "_blank" : undefined}
                            rel={link.external ? "noopener noreferrer" : undefined}
                            data-testid={link.testId}
                            onClick={(e) => handleNavClick(e, link)}
                            className="text-[13.5px] font-body font-medium text-slate-700 hover:text-teal-700 transition-colors whitespace-nowrap"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={awsaiUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-cta-explore-awsai"
                        onClick={() => trackCta("awsai_click", { source: "nav" })}
                        className="btn-base btn-primary"
                    >
                        Explore AWSAI <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                    </a>
                </div>

                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setMobileOpen((v) => !v)}
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-slate-900 hover:text-teal-700 transition-colors"
                    aria-label="Toggle navigation"
                >
                    {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {mobileOpen && (
                <div data-testid="nav-mobile-panel" className="lg:hidden bg-white border-t border-slate-200 px-6 py-6">
                    <ul className="flex flex-col gap-1">
                        {navLinks.map((link) => (
                            <li key={link.target}>
                                <a
                                    href={hrefFor(link)}
                                    target={link.external ? "_blank" : undefined}
                                    rel={link.external ? "noopener noreferrer" : undefined}
                                    onClick={(e) => handleNavClick(e, link)}
                                    className="block py-3 text-[15px] font-body font-medium text-slate-800 hover:text-teal-700 transition-colors border-b border-slate-100"
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <a
                        href={awsaiUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-mobile-cta"
                        onClick={() => trackCta("awsai_click", { source: "nav_mobile" })}
                        className="btn-base btn-primary w-full mt-5"
                    >
                        Explore AWSAI <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>
            )}
        </header>
    );
};

export default Navigation;
